import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, MessageCircle } from 'lucide-react';
import { apiService } from '@/services/api';
import styles from './ForwardDestinationModal.module.scss';

export interface ForwardDestinationModalProps {
  visible: boolean;
  onClose: () => void;
  sourceType: 'dm' | 'group';
  sourceMessageId: string;
  /** When forwarding from a DM chat, exclude this friend (current chat) */
  excludeFriendId?: string;
  /** When forwarding from a group chat, exclude this group (current group) */
  excludeGroupId?: string;
  /** Called to perform the forward; modal stays open until this resolves, then onClose is typically called by parent */
  onForward: (params: {
    destinationType: 'dm' | 'group';
    destinationFriendId?: string;
    destinationGroupId?: string;
  }) => Promise<void>;
}

interface FriendItem {
  id: string;
  displayName: string;
  profilePic?: string;
}

interface GroupItem {
  id: string;
  name: string;
  profilePicUrl?: string;
}

export function ForwardDestinationModal({
  visible,
  onClose,
  sourceType,
  sourceMessageId,
  excludeFriendId,
  excludeGroupId,
  onForward,
}: ForwardDestinationModalProps) {
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forwarding, setForwarding] = useState(false);

  useEffect(() => {
    if (!visible) return;

    setLoading(true);
    setError(null);

    Promise.all([
      apiService.getFriends().then((res) => {
        if (!res.success || !res.data) return [];
        const data = res.data as any;
        const list = data.friends ?? (Array.isArray(data) ? data : []);
        return list.map((f: any) => {
          const profile = f.friendProfile ?? f.profile ?? f;
          return {
            id: profile.id ?? profile.userId,
            displayName:
              (profile.displayName ??
                [profile.firstName, profile.lastName].filter(Boolean).join(' ')) || 'Friend',
            profilePic: profile.profilePic ?? profile.profilePicture,
          };
        });
      }),
      apiService.getGroups(1, 100).then((res) => {
        if (!res.success || !res.data) return [];
        const list = Array.isArray(res.data) ? res.data : [];
        return list.map((g: any) => ({
          id: g.id,
          name: g.name ?? 'Group',
          profilePicUrl: g.profilePicUrl ?? g.profile_pic_url ?? g.avatarUrl,
        }));
      }),
    ])
      .then(([friendsList, groupsList]) => {
        setFriends(friendsList);
        setGroups(groupsList);
      })
      .catch((err) => {
        setError(err?.message ?? 'Failed to load friends and groups');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [visible]);

  const handleSelectFriend = (friendId: string) => {
    setForwarding(true);
    onForward({
      destinationType: 'dm',
      destinationFriendId: friendId,
    })
      .then(() => {
        onClose();
      })
      .catch((err) => {
        setForwarding(false);
        setError(err?.message ?? 'Failed to forward');
      });
  };

  const handleSelectGroup = (groupId: string) => {
    setForwarding(true);
    onForward({
      destinationType: 'group',
      destinationGroupId: groupId,
    })
      .then(() => {
        onClose();
      })
      .catch((err) => {
        setForwarding(false);
        setError(err?.message ?? 'Failed to forward');
      });
  };

  const filteredFriends = excludeFriendId
    ? friends.filter((f) => f.id !== excludeFriendId)
    : friends;
  const filteredGroups = excludeGroupId
    ? groups.filter((g) => g.id !== excludeGroupId)
    : groups;

  if (!visible) return null;

  const content = (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="forward-modal-title">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 id="forward-modal-title">Forward to</h3>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className={`${styles.content} ${forwarding ? styles.forwarding : ''}`}>
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              Loading…
            </div>
          )}
          {error && <div className={styles.error}>{error}</div>}
          {!loading && !error && (
            <>
              {filteredFriends.length > 0 && (
                <>
                  <p className={styles.sectionTitle}>
                    <MessageCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Friends
                  </p>
                  <ul className={styles.list}>
                    {filteredFriends.map((f) => (
                      <li key={f.id}>
                        <button
                          type="button"
                          className={styles.item}
                          onClick={() => handleSelectFriend(f.id)}
                          disabled={forwarding}
                        >
                          <img
                            src={f.profilePic || 'https://www.gravatar.com/avatar?d=mp&f=y'}
                            alt=""
                            className={styles.avatar}
                          />
                          <span className={styles.name}>{f.displayName}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {filteredGroups.length > 0 && (
                <>
                  <p className={styles.sectionTitle}>
                    <Users size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Groups
                  </p>
                  <ul className={styles.list}>
                    {filteredGroups.map((g) => (
                      <li key={g.id}>
                        <button
                          type="button"
                          className={styles.item}
                          onClick={() => handleSelectGroup(g.id)}
                          disabled={forwarding}
                        >
                          {g.profilePicUrl ? (
                            <img
                              src={g.profilePicUrl}
                              alt=""
                              className={styles.groupAvatar}
                            />
                          ) : (
                            <div className={styles.groupIcon}>
                              <Users size={20} />
                            </div>
                          )}
                          <span className={styles.name}>{g.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {filteredFriends.length === 0 && filteredGroups.length === 0 && (
                <div className={styles.empty}>
                  No friends or groups to forward to.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default ForwardDestinationModal;
